import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import AccessoriesServices from '@Services/Accessories.services.ts';
import { Accessory } from '@Types/Asset.types';
import { NewAccessoryFields } from '@Types/Form.types';
import { formatRequired } from '@Utils/form.utils';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import { ErrorDisplay, FreeText, Toast } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

type Props = {
  show: boolean;
  accessory?: Accessory;
  onClose: () => void;
  onSuccess: () => void;
};

const { ACCESSORY } = VALIDATION_RULES;

const AccessoryModal = ({ show, accessory, onClose, onSuccess }: Props) => {
  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
    setValue,
  } = useForm<NewAccessoryFields>();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    reset();
    if (accessory) {
      setValue('name', accessory.name);
    }
  }, [accessory, show]);

  const onSubmitSuccess = () => {
    Toast.success(accessory ? 'Accessory updated successfully.' : 'Accessory created successfully.');
    onSuccess();
    reset();
    onClose();
  };

  const createAccessory = (fields: NewAccessoryFields) => {
    AccessoriesServices.createAccessory(fields)
      .then(() => onSubmitSuccess())
      .catch((errorResponse) => handleRequestError(errorResponse, setErrorMessage, true));
  };

  const updateAccessory = (fields: NewAccessoryFields) => {
    AccessoriesServices.updateAccessory(accessory?.id, fields)
      .then(() => onSubmitSuccess())
      .catch((errorResponse) => handleRequestError(errorResponse, setErrorMessage, true));
  };

  const title = accessory ? 'Edit Accessory' : 'Add Accessory';
  const handleAssetStatusSubmit = accessory ? updateAccessory : createAccessory;

  return (
    <div className='modal--fit-content modal--w-30vw modal--pt-25vh'>
      <ModalWrapper
        title={title}
        show={show}
        handleClose={onClose}
        primaryButtonText='Confirm'
        primaryAction={() => handleSubmit(handleAssetStatusSubmit)()}>
        {errorMessage ? (
          <ErrorDisplay inline details={errorMessage} />
        ) : (
          <form onSubmit={() => handleSubmit(handleAssetStatusSubmit)()}>
            <div className='grid'>
              <div className='col-12'>
                <FreeText
                  label={formatRequired('Name')}
                  fullWidth
                  error={!!errors.name}
                  errorMessage={errors.name?.message}
                  {...register('name', ACCESSORY.NAME)}
                />
                <br />
              </div>
            </div>
          </form>
        )}
      </ModalWrapper>
    </div>
  );
};

export default AccessoryModal;
